import React from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Modal, InputText, Button, Select, Toast, NAICS, SICSearch } from '@insurancemenu/imenu-components-lib';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { useRootStore } from 'store/rootStore';
import { createStyles } from './styles';

interface Props {
  show: boolean;
  onClose: () => void;
  onAction: (e: React.SyntheticEvent) => void;
}

const PlanDetailsModal: React.FC<Props> = observer(({ show, onClose, onAction }) => {
  const classes = createStyles();
  // Mobx store
  const rootStore = useRootStore();
  const { browsingModalsStore, geoStore, domainStoreV2 } = rootStore;
  const { form, effectiveDates, canBrowseNext, groupSizeValidation, overrideNAICSValue, overrideSICSearchValue } =
    browsingModalsStore;

  // This is done in order to deserialize the mobx properties in order for them to be read by the children components.
  const convertedEffectiveDates = effectiveDates.map((dateObj) => toJS(dateObj)).slice();
  const counties = geoStore.countiesAsSelectOptions.map((dateObj) => toJS(dateObj)).slice();
  const stateFieldValue = form.fields.state.value;
  const newProposalAdditionalInputs = toJS(domainStoreV2.domainHeader).properties?.newProposalAdditionalInputs;

  // Component actions
  function onToastClose(): void {
    geoStore.setWrongZipCodeStatus(false);
  }

  function onModalClose(): void {
    geoStore.setWrongZipCodeStatus(false);
    onClose();
  }

  function getAdditionalInputLabel(inputType: string): string {
    return inputType + (newProposalAdditionalInputs?.required.includes(inputType) ? '' : ' (optional)');
  }

  function renderAdditionalInputs(): JSX.Element[] {
    // Each component in the store form has its own optional status
    const componentTypes: string[] = [
      ...(newProposalAdditionalInputs?.required || []),
      ...(newProposalAdditionalInputs?.optional || []),
    ];
    const components: JSX.Element[] = [];

    componentTypes.forEach((componentType) => {
      if (componentType === 'NAICS') {
        components.push(
          // Note: NAICS component does not support optional status properly.
          // Although incorrect values will not be sent to the backend, they will still be displayed in the component.
          <NAICS
            label={getAdditionalInputLabel(componentType)}
            onCodeUpdate={overrideNAICSValue}
            className={classes.inputSearchComponent}
            width="100%"
            key="naics"
          />,
        );
      }
      if (componentType === 'SIC') {
        components.push(
          <SICSearch
            label={getAdditionalInputLabel(componentType)}
            className={classes.inputSearchComponent}
            width="100%"
            onCodeUpdate={overrideSICSearchValue}
            key="sic"
          />,
        );
        if (browsingModalsStore.ineligibleSicCode) {
          components.push(
            <p className={classes.ineligibleSicCodeMessage}>
              Healthcare Professional (8011, 8021, 8031, 8041, 8042, 8043, 8049, 8071, 8072, 8092, 8093) groups are not
              supported on Insurance Menu. Your request for proposal (RFP) can be emailed to{' '}
              <a href="mailto:digital@sunlife.com"> digital@sunlife.com</a>.
            </p>,
          );
        }
      }
      if (componentType.includes('GROUP_NUMERIC_SIZE')) {
        components.push(
          <InputText
            key="groupSize"
            value={form.fields.groupSize.value}
            onChange={form.onFieldChange}
            name="groupSize"
            label="Estimated Number of Eligible Employees"
            maxLength={2}
            className={classes.formSelect}
            validationMessage={
              form.fields.groupSize.value.length > 0 && groupSizeValidation.length > 0 ? groupSizeValidation : ''
            }
            validationPassed={form.fields.groupSize.value.length === 0 || groupSizeValidation.length === 0}
          />,
        );
      }
    });
    return components;
  }

  return (
    <Modal show={show} onClose={onModalClose} className={classes.modalSpacing} id="plan-details">
      <div className={classes.body}>
        <h3 className={classes.title}>Plan Details</h3>
        <p className={classes.subtitle}>{browsingModalsStore.rootStore.groupStore.group.name}</p>
        <form className={classes.form} onSubmit={onAction} id="plan_details_form">
          <Select
            label="Effective Date"
            name="effectiveDate"
            options={convertedEffectiveDates}
            value={form.fields.effectiveDate.value}
            onChange={form.onFieldChange}
            className={classes.formSelect}
          />
          <div className={classes.inputContainer}>
            <InputText
              label="Zip Code"
              name="zipCode"
              value={form.fields.zipCode.value}
              maxLength={5}
              className={classes.formZipCodeInput}
              onChange={form.onFieldChange}
            />
            <Select
              label="County"
              name="county"
              value={form.fields.county.value}
              className={classes.formCountyInput}
              options={counties}
              onChange={form.onFieldChange}
              validationPassed={form.fields.zipCode.value.length === 0 || !form.fields.county.unpassedValidationMsg}
            />
            <InputText label="State" name="state" disabled value={stateFieldValue} className={classes.formStateInput} />
          </div>
          {browsingModalsStore.ineligibleState && (
            <p className={classes.ineligibleStateMessage}>Ineligible state to browse</p>
          )}
          {renderAdditionalInputs()}
        </form>
      </div>
      <footer className={classes.footer}>
        <div className={classes.actionsBlock}>
          <Button type="outlined" onClick={onModalClose} size="large">
            Cancel
          </Button>
          <Button color="primary" htmlType="submit" size="large" htmlForm="plan_details_form" disabled={!canBrowseNext}>
            Next
          </Button>
        </div>
      </footer>
      <FormsSpinner display={browsingModalsStore.loading} />
      <Toast
        show={geoStore.wrongZipCodeKeyedIn}
        onClose={onToastClose}
        colorType={ToastColorTypes.WARNING}
        className={classes.toastMessage}
      >
        The keyed in zip code is not valid!
      </Toast>
    </Modal>
  );
});

export default PlanDetailsModal;
